import { DateTime } from '@ksyos/date-time';
import { Card, Form, FormGroup, FormParagraph, PageHeader } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { ServiceCode } from '@common/enums';
import type { IDeviceStatus } from '@common/interfaces/deviceStatus';
import type { IDeviceType } from '@common/interfaces/deviceType';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import type { IDeviceStatusOrganization } from './DevicesList';
import { DeviceInformationCard } from './DeviceInformationCard';
import { DeviceStatusCard } from './DeviceStatusCard';

export interface IDevice {
    created_at: string;
    deviceStatus: IDeviceStatus;
    deviceStatusOrganizations: IDeviceStatusOrganization[] | null;
    deviceType: IDeviceType;
    deviceTypeId: string;
    externalId: string;
    id: string;
    lastSuccessfulTcohRecording: Date | null;
    notes: string | null;
    numberOfSuccessfulTcohRecordings: number;
}
export function DeviceDetails() {
    const translate = getSelectedLanguage();

    const { id } = useParams();
    const [device, setDevice] = useState<IDevice>();
    const [deviceTypeServiceCodes, setDeviceTypeServiceCodes] = useState<ServiceCode[]>([]);

    useEffect(() => {
        loadDevice();
    }, [id]);

    if (!device || !device.deviceType) {
        return null;
    }

    return (
        <PageTemplate width="small">
            <PageHeader title={translate.devices.details} />
            <DeviceInformationCard
                device={device}
                onSave={loadDevice}
                serviceCodes={deviceTypeServiceCodes}
            />
            <Card>
                <Card.Transition type={'read'} />
                <Card.Header title={translate.devices.usage} />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {renderLabel(
                            translate.devices.registered,
                            DateTime.from(device.created_at).format('DD-MM-YYYY')
                        )}
                        {renderLabel(
                            translate.devices.lastUsed,
                            device.lastSuccessfulTcohRecording
                                ? DateTime.from(device.lastSuccessfulTcohRecording).format('DD-MM-YYYY')
                                : translate.devices.noDataAvailable
                        )}
                        {renderLabel(
                            translate.devices.numberOfRecordings,
                            device.numberOfSuccessfulTcohRecordings
                                ? device.numberOfSuccessfulTcohRecordings.toString()
                                : translate.devices.noDataAvailable
                        )}
                    </Form>
                </Card.Body>
            </Card>

            <DeviceStatusCard
                device={device}
                reloadDevice={async () => await loadDevice()}
            />
        </PageTemplate>
    );

    async function loadDevice() {
        const result = await fetchJson({ method: 'GET', url: `/api/devices/${id}` });
        const deviceTypes = await loadDeviceTypes();
        const deviceType = await deviceTypes?.find((item: IDeviceType) => item.id === result.json.deviceTypeId);

        const resultData = {
            ...result.json,
            deviceType,
        };

        setDevice(resultData);

        setDeviceTypeServiceCodes([
            ...new Set(
                [...resultData.deviceType.consumingServices, ...resultData.deviceType.providingServices].map(
                    (service) => service.code
                )
            ),
        ]);
    }

    async function loadDeviceTypes() {
        const result = await fetchJson({ method: 'GET', url: '/api/deviceTypes' });

        return result.json.items;
    }

    function renderLabel(label: string, value?: string | null) {
        const displayValue = value ?? '-';

        return (
            <FormGroup label={label}>
                <FormParagraph>{displayValue}</FormParagraph>
            </FormGroup>
        );
    }
}
